import { EImportSectionsTarget } from '@constants';
import { IActivity } from '@sdk/contracts';
import { Empty, Flex, Spin } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Wrapper, EmptyActivities, StyledCollapse, StyledTag } from './ActivitiesList.styles';
import { ActivityItem } from './ActivityItem';

import { Icon } from '@/components/Icon';
import { Box } from '@/components/Utils';
import { useSectionCourseDetail } from '@/store';

type ActivityListProps = {
  courseId?: string;
  selectedActivities: IActivity[];
  onSelectionChange: (activities: IActivity[]) => void;
  target: EImportSectionsTarget;
};

export const ActivitiesList: React.FC<ActivityListProps> = ({
  courseId,
  selectedActivities,
  onSelectionChange,
  target
}) => {
  const { t } = useTranslation('ManagerBaseContentDetailTranslations');
  const { course, isLoading } = useSectionCourseDetail(courseId);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const isSelected = (activityId: string) => selectedActivities.some(a => a._id === activityId);

  const handleCheckboxChange = (activity: IActivity, checked: boolean) =>
    onSelectionChange(
      checked
        ? [...selectedActivities, activity]
        : selectedActivities.filter(a => a._id !== activity._id)
    );

  if (!courseId) return null;

  if (isLoading) {
    return (
      <Wrapper>
        <Flex justify="center" align="center" style={{ padding: 32 }}>
          <Spin />
        </Flex>
      </Wrapper>
    );
  }

  const sections =
    target === EImportSectionsTarget.INTRODUCTION ? course?.introduction : course?.sections;

  if (!sections?.length) {
    return (
      <Wrapper>
        <Empty description={t('NO_SECTIONS_AVAILABLE')} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {sections.map((section, index) => {
        const isFirst = index === 0;
        const isLast = index === sections.length - 1;

        return (
          <StyledCollapse
            key={section._id}
            $isFirst={isFirst}
            $isLast={isLast}
            activeKey={activeKeys}
            onChange={keys => setActiveKeys(keys)}
            expandIcon={({ isActive }) => (
              <Box gap={10}>
                <Icon i={isActive ? 'CaretDown' : 'CaretRight'} size={16} />
              </Box>
            )}
            items={[
              {
                key: section._id,
                label: (
                  <Flex gap={8} align="center">
                    <div>{section.name}</div>
                    <StyledTag>
                      {t('ACTIVITIES', { count: section?.activities?.length ?? 0 })}
                    </StyledTag>
                  </Flex>
                ),
                children: section.activities?.length ? (
                  section.activities.map(activity => (
                    <ActivityItem
                      key={activity._id}
                      activity={activity}
                      isSelected={isSelected(activity._id)}
                      onCheckboxChange={handleCheckboxChange}
                    />
                  ))
                ) : (
                  <EmptyActivities>{t('NO_ACTIVITIES_IN_SECTION')}</EmptyActivities>
                )
              }
            ]}
          />
        );
      })}
    </Wrapper>
  );
};
